import {
	getFormProps,
	getInputProps,
	getSelectProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Form } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { FeedbackEmail } from '#app/components/email/FeedbackEmail.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { type Route } from './+types/feedback.ts'

const CATEGORIES = [
	'Comment',
	'Suggestion',
	'Area for Improvement',
	'Bug Report',
	'Other',
] as const

const FeedbackSchema = z.object({
	category: z.enum(CATEGORIES),
	name: z.string().max(100).optional(),
	email: z
		.string()
		.email('Enter a valid email')
		.max(100)
		.optional()
		.or(z.literal('')),
	message: z
		.string({ required_error: 'Please share a few words' })
		.min(5, 'Please share a few words')
		.max(5000, 'That is a bit long — please keep it under 5000 characters'),
})

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const user = userId
		? await prisma.user.findUnique({
				where: { id: userId },
				select: { display: true, primaryEmail: true },
			})
		: null
	return {
		defaultName: user?.display ?? '',
		defaultEmail:
			user?.primaryEmail && user.primaryEmail !== 'missing'
				? user.primaryEmail
				: '',
	}
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = parseWithZod(formData, { schema: FeedbackSchema })
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { category, name, email, message } = submission.value
	const response = await sendEmail({
		to: 'support@clearwaterfarmsunit1.com',
		subject: `Clearwater Farms 1 Feedback: ${category}`,
		react: (
			<FeedbackEmail
				name={name}
				email={email || undefined}
				category={category}
				message={message}
			/>
		),
	})

	if (response.status === 'success') {
		return redirectWithToast(
			'/feedback',
			{
				type: 'success',
				title: 'Thank you!',
				description: 'Your feedback has been sent to the board.',
			},
			{ status: 303 },
		)
	}

	return data(
		{ result: submission.reply({ formErrors: [response.error.message] }) },
		{ status: 500 },
	)
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Share Feedback | Clearwater Farms 1' }]
}

export default function FeedbackRoute({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'feedback-form',
		constraint: getZodConstraint(FeedbackSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: FeedbackSchema })
		},
		shouldRevalidate: 'onBlur',
		defaultValue: {
			category: 'Comment',
			name: loaderData.defaultName,
			email: loaderData.defaultEmail,
		},
	})

	return (
		<div className="container pt-20 pb-32">
			<div className="mx-auto flex max-w-xl flex-col">
				<div className="text-center">
					<Icon
						name="sms"
						className="text-primary mx-auto h-12 w-12"
						aria-hidden="true"
					/>
					<h1 className="text-h1 mt-2">We'd love your feedback</h1>
					<p className="text-body-md text-muted-foreground mt-3">
						Comments, suggestions, areas for improvement — tell us what's on
						your mind. It goes straight to the board.
					</p>
				</div>

				<div className="mt-10">
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />

						<div>
							<Label htmlFor={fields.category.id}>
								What kind of feedback is this?
							</Label>
							<select
								{...getSelectProps(fields.category)}
								className="border-input bg-background ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
							>
								{CATEGORIES.map((category) => (
									<option key={category} value={category}>
										{category}
									</option>
								))}
							</select>
							<div className="min-h-[32px] px-4 pt-1 pb-3">
								<ErrorList
									id={fields.category.errorId}
									errors={fields.category.errors}
								/>
							</div>
						</div>

						<TextareaField
							labelProps={{ children: 'Your feedback' }}
							textareaProps={{
								...getTextareaProps(fields.message),
								rows: 6,
								placeholder:
									'Share your comments, suggestions, or ideas for improvement…',
								autoFocus: true,
							}}
							errors={fields.message.errors}
						/>

						<div className="grid gap-x-4 sm:grid-cols-2">
							<Field
								labelProps={{ children: 'Name (optional)' }}
								inputProps={{
									...getInputProps(fields.name, { type: 'text' }),
									placeholder: 'Your name',
								}}
								errors={fields.name.errors}
							/>
							<Field
								labelProps={{ children: 'Email (optional, for a reply)' }}
								inputProps={{
									...getInputProps(fields.email, { type: 'text' }),
									placeholder: 'Where can we reach you?',
								}}
								errors={fields.email.errors}
							/>
						</div>

						<ErrorList errors={form.errors} id={form.errorId} />

						<StatusButton
							className="mt-2 w-full"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Send Feedback
						</StatusButton>
					</Form>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
